import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, initializeApi } from './api.js';
import { useToasts } from './ToastContext.jsx';
import ConfirmationModal from './ConfirmationModal.jsx';
import { Loader2 } from "lucide-react";

/** Utils */
function parseJwtExp(token) {
  try {
    const [, payload] = token.split('.');
    const { exp } = JSON.parse(atob(payload));
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const [token, setToken] = useState(() => localStorage.getItem('tvk_token') || "");
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('tvk_refresh') || "");
  const [role, setRole] = useState(() => localStorage.getItem('tvk_role') || 'member');
  const [refreshing, setRefreshing] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { token: newToken, refreshToken: newRefreshToken } = await apiFetch('/auth/refresh', { method: 'POST', body: { refreshToken } });
      authBridge.setTokens(newToken, newRefreshToken);
    } catch (err) {
      performLogout();
    } finally { setRefreshing(false); }
  }, [refreshing, refreshToken]);

  const performLogout = useCallback(() => {
    setToken(""); setRefreshToken(""); localStorage.removeItem('tvk_token'); localStorage.removeItem('tvk_refresh'); localStorage.removeItem('tvk_role');
    setIsLogoutConfirmOpen(false);
    navigate('/login');
  }, [navigate]);
  const logout = useCallback(() => setIsLogoutConfirmOpen(true), []);

  const authBridge = {
    getToken: () => token,
    getRefresh: () => refreshToken,
    setTokens: (t, r) => { setToken(t || ""); setRefreshToken(r || ""); localStorage.setItem('tvk_token', t || ""); localStorage.setItem('tvk_refresh', r || ""); },
    setRole: (rr) => { setRole(rr || 'member'); localStorage.setItem('tvk_role', rr || 'member'); },
    setRefreshing: (v) => setRefreshing(!!v),
    refresh,
    onUnauthorized: logout
  };
  initializeApi(authBridge);

  const { data: user, error: userError, isLoading: userLoading, isError: isUserError, refetch: refetchUser } = useQuery({
    queryKey: ['user', token],
    queryFn: () => apiFetch('/me'),
    enabled: !!token && !refreshing,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    initializeApi(authBridge);
  }, [token, refreshToken]);

  useEffect(() => {
    if (isUserError && userError?.status === 401) {
      logout();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserError, userError, logout]);

  useEffect(() => {
    let timeoutId;
    const checkAndRefreshToken = async () => {
      const expMs = parseJwtExp(token);
      if (!expMs || refreshing) return;
      const dt = expMs - Date.now();
      if (dt < 87880 && refreshToken) {
        refresh();
      } else if (dt > 0) {
        timeoutId = setTimeout(checkAndRefreshToken, dt - 87880);
      }
    };
    checkAndRefreshToken();
    return () => clearTimeout(timeoutId);
  }, [token, refreshToken, refreshing, role, logout]);

  const login = async (credentials, rememberMe) => {
    try {
      const response = await apiFetch('/auth/login', { method: 'POST', body: credentials, rawResponse: true });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Login failed with status ${response.status}. Server says: ${errorText.substring(0, 100)}`);
      }
      const { accessToken, refreshToken: rt, role: r } = await response.json();
      authBridge.setTokens(accessToken, rt);
      authBridge.setRole(r);

      if (rememberMe) {
        localStorage.setItem('tvk_remember_phone', credentials.phone);
      } else {
        localStorage.removeItem('tvk_remember_phone');
      }

      initializeApi(authBridge);
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      navigate('/');
    } catch (error) {
      console.error("Login process failed:", error);
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        push("சேவையகத்தை அணுக முடியவில்லை. உங்கள் நெட்வொர்க்கைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.", "error");
      } else {
        push(error.message || "உள்நுழைவின் போது ஒரு பிழை ஏற்பட்டது. மீண்டும் முயற்சிக்கவும்.", "error");
      }
      throw error;
    }
  };

  const value = { token, refreshToken, role, user, userLoading, isUserError, login, logout, refreshing, refetchUser };

  return (
    <AuthCtx.Provider value={value}>
      {children}
      {refreshing && (
        <div className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow p-4 flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">அங்கீகாரம் புதுப்பிக்கப்படுகிறது…</span>
          </div>
        </div>
      )}
      {isLogoutConfirmOpen && (
        <ConfirmationModal
          title="வெளியேறுவதை உறுதிப்படுத்தவும்"
          message="நீங்கள் நிச்சயமாக வெளியேற விரும்புகிறீர்களா?"
          onConfirm={performLogout}
          onCancel={() => setIsLogoutConfirmOpen(false)}
        />
      )}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthCtx);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};